import { useBreakpointValue } from '@chakra-ui/react';
import {
  addDays,
  differenceInHours,
  eachDayOfInterval,
  format,
  isSameDay,
  startOfDay,
  subDays,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { MindbodyClass } from 'types/class';
import { ClientVisit } from 'types/client';
import { ExpirationUnitTranslations } from 'types/services';

const numberOfDays = mondayMindbody?.customization?.numberOfDays ?? 8;

/**
 * Generates an array of available dates for booking.
 *
 * Fenêtre glissante quotidienne ancrée à midi : de `jour J 00h00` à `jour J + numberOfDays` inclus.
 * Chaque jour à 12h, la fenêtre avance d'un jour (un nouveau jour s'ouvre, le plus ancien sort).
 * Avant 12h, on reste sur la fenêtre du jour précédent (rollover à midi pile).
 * Ex. avec numberOfDays = 13 → 14 jours visibles ; numberOfDays = 15 → 16 jours visibles.
 * @returns Date[]
 */
export function getBookableDays() {
  const now = new Date();
  const today = startOfDay(now);
  const isPastNoon = now.getHours() >= 12;

  const start = isPastNoon ? today : subDays(today, 1);
  return eachDayOfInterval({
    start,
    end: addDays(start, numberOfDays),
  });
}

export function isClassBookable(mindbodyClass: MindbodyClass) {
  if (mindbodyClass.IsCourse) {
    return new Date(mindbodyClass.StartDateTime) > new Date();
  }
  return (
    new Date(mindbodyClass.StartDateTime) > new Date() &&
    getBookableDays().some((bookableDay) => isSameDay(bookableDay, new Date(mindbodyClass.StartDateTime)))
  );
}

export function getClassRemainingCapacity(mindbodyClass: MindbodyClass) {
  return mindbodyClass.MaxCapacity - mindbodyClass.TotalBooked;
}

export function classHasCapacity(mindbodyClass: MindbodyClass) {
  return getClassRemainingCapacity(mindbodyClass) > 0;
}

/** Fenêtre d'annulation par défaut quand le back ne fournit rien (ancien comportement). */
const DEFAULT_LATE_CANCEL_HOURS = 24;

/**
 * Fenêtre d'annulation (heures) du site d'une visite.
 *
 * Source de vérité : `mondayMindbody.lateCancelHoursBySite` (calculé par
 * Frontend.php à partir de l'option BO « Fenêtre d'annulation » et des défauts
 * du code, pour chaque site de la région). Repli sur
 * `customization.lateCancelHours` (site courant), puis 24 h si le bundle
 * tourne sur un back pas à jour.
 */
export function getLateCancelHours(siteId?: number | string | null): number {
  const key = siteId === undefined || siteId === null ? String(mondayMindbody?.siteId ?? '') : String(siteId);
  const candidates = [
    mondayMindbody?.lateCancelHoursBySite?.[key],
    key === String(mondayMindbody?.siteId ?? '') ? mondayMindbody?.customization?.lateCancelHours : undefined,
  ];
  for (const candidate of candidates) {
    const hours = Number(candidate);
    if (Number.isFinite(hours) && hours > 0) return hours;
  }
  return DEFAULT_LATE_CANCEL_HOURS;
}

/**
 * Annulation tardive (crédit perdu) : strictement moins de N heures pleines
 * avant le cours, N = fenêtre du site de la visite. Même règle que
 * Helper::isVisitLateCancel côté serveur, qui décide du flag envoyé à Mindbody.
 */
export function isVisitLateCancel(visit: ClientVisit) {
  const start = new Date();
  const end = new Date(visit.StartDateTime);
  if (end <= start) return true;

  return differenceInHours(end, start) < getLateCancelHours(visit.SiteId);
}

export const formatExpiration = (length: number, unit: string | undefined) => {
  if (!length || !unit) return '';

  const unitTranslations: ExpirationUnitTranslations = {
    day: { singular: 'jour', plural: 'jours' },
    days: { singular: 'jour', plural: 'jours' },
    month: { singular: 'mois', plural: 'mois' },
    months: { singular: 'mois', plural: 'mois' },
  };

  const { singular, plural } = unitTranslations[unit.toLowerCase()] || { singular: '', plural: '' };
  const unitText = length > 1 ? plural : singular;

  return `Valable ${length} ${unitText}`;
};

export function isValidEmail(email: string) {
  // eslint-disable-next-line no-useless-escape
  return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email,
  );
}

export const getDayAbbreviation = (date: Date) => {
  const day = format(date, 'EEEE', { locale: fr });
  switch (day) {
    case 'lundi':
      return 'L';
    case 'mardi':
      return 'Ma';
    case 'mercredi':
      return 'Me';
    case 'jeudi':
      return 'J';
    case 'vendredi':
      return 'V';
    case 'samedi':
      return 'S';
    case 'dimanche':
      return 'D';
    default:
      return '';
  }
};

export const toSlug = (text: string) => {
  return text
    .toString() // Convertit en chaîne, au cas où
    .normalize('NFD') // Décompose les caractères accentués en composants (NFD mode)
    .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
    .toLowerCase() // Convertit en minuscules
    .trim() // Supprime les espaces au début et à la fin
    .replace(/[^a-z0-9 -]/g, '') // Supprime les caractères non alphanumériques
    .replace(/\s+/g, '-') // Remplace les espaces par des traits d'union
    .replace(/-+/g, '-'); // Remplace les séquences multiples de trait d'union par un seul trait d'union
};

export const capitalizeFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const stripHtml = (html: string) => {
  const temporalDivElement = document.createElement('div');
  temporalDivElement.innerHTML = html;
  return temporalDivElement.textContent || temporalDivElement.innerText || '';
};

export function useIsMobile() {
  const isMobile = useBreakpointValue({ base: true, md: false });
  return isMobile;
}
